import type { InSnipCard } from "./types";

/**
 * The "Good News" franchise, a daily bright-side beat.
 *
 * A card belongs on the shelf when, and only when, the pipeline's judge put it there. There is no
 * client-side word list any more, and removing it is the entire point of this file's history.
 *
 * What the word list did, live, on 2026-08-23:
 *
 *   "Canada hits back with tariffs on US goods from Sept 8"
 *   "Father leaves seven-year-old alone on Mount Fuji while climbing higher"
 *
 * were the two cards under the heading "2 genuinely good stories today". Alongside them the same
 * rule had flagged a house fire that hospitalised a man ("rescue" appeared in the body), a fire
 * service pay dispute ("rescue" in the headline), and a cat charity being overwhelmed by
 * surrenders ("rescue" is the charity's NAME).
 *
 * Every one of those is the same defect: WORD PRESENCE IS A PROXY. Whether a reader finishes a
 * story better off is the thing, and no list of words decides it. "Nine teen apprentices run into
 * a burning house and rescue a woman" and "Unanderra house fire hospitalizes man with serious
 * burns" share almost all their vocabulary and are opposite stories.
 *
 * So the judging happens once, server-side, in jobs/good_news.py, where an agent reads the story
 * and a record of every verdict is kept. The client reads the result. A fallback heuristic here
 * would silently overrule that judge on the one surface the reader actually sees, which is worse
 * than an empty shelf: an empty shelf is visibly empty, a wrong shelf looks fine.
 */

/**
 * True when the pipeline's judge confirmed this card for the franchise.
 *
 * THE TAG, AND ONLY THE TAG. `goodNews: true` on its own means something NOMINATED this card and
 * no judge has looked yet: the authoring agent's opinion, or the provenance of the good-news
 * lane. Only jobs/good_news.py adds the tag, and it adds it only after reading the story. Reading
 * the boolean here would put every nomination straight on the shelf, which is the failure this
 * file exists to prevent, reintroduced by the back door.
 */
export function isFlaggedGoodNews(card: InSnipCard): boolean {
  return (card.tags ?? []).some((t) => t.trim().toLowerCase() === "good news");
}

/**
 * True for a confirmed bright-side story.
 *
 * Kept as a separate name from isFlaggedGoodNews because callers mean different things by them
 * (one asks "does this belong on the shelf", the other "was it judged"), and they were allowed to
 * diverge once. They no longer can.
 */
export function isGoodNews(card: InSnipCard): boolean {
  // A brief or a quiz is never a franchise card, whatever tag it arrives carrying.
  if (card.contentType === "brief" || card.contentType === "quiz") return false;
  return isFlaggedGoodNews(card);
}

/**
 * What the shelf can honestly call itself.
 *
 * The subtitle read "N genuinely good stories today" while N counted a pool assembled from
 * whatever rounds happened to be loaded, so it was already describing a span as a day. The
 * server shelf makes that worse on purpose: it reaches back three days, because the judge
 * (jobs/good_news.py) first runs at 14:10 and no card can carry today's date before then. A
 * reader opening the panel at breakfast would otherwise be told that yesterday's rescues
 * happened today.
 *
 * So `today` is counted server-side and passed separately, and this says only what is true:
 *
 *   today > 0   "3 good stories today, 2 more from the last few days"
 *   today = 0   "Today's are still being judged, 4 good stories from the last 3 days"
 *   unknown     no day claim at all (the fetch failed; the deck's own cards are all we have)
 *
 * @param count  cards on the shelf
 * @param today  how many carry today's Sydney date, or null when the server was not reached
 * @param days   the width of the server's window, in days
 */
export function shelfLabel(count: number, today: number | null, days: number): string {
  if (count <= 0) return "A daily beat of genuinely good stories";
  const story = (k: number) => (k === 1 ? "story" : "stories");
  if (today === null) return `${count} genuinely good ${story(count)} · tap any to read`;
  if (today > 0) {
    const rest = count > today ? `, ${count - today} more from the last few days` : "";
    return `${today} good ${story(today)} today${rest} · tap any to read`;
  }
  const span = days > 1 ? `the last ${days} days` : "yesterday";
  return `Today's are still being judged · ${count} good ${story(count)} from ${span}`;
}
